import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Card } from "@/components/ui/card";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ArrowLeft, Send, ShieldAlert } from "lucide-react";
import { DatePicker } from "@/components/DatePicker";
import { toast } from "sonner";
import {
  CHANGE_TYPES, EMPLOYMENT_STATUS, PAY_FREQ, JOB_ROWS, PAY_BASIS, ADJ_TYPES, ADJ_FREQ,
  LEAVE_ACTIONS, LEAVE_PAY, TERM_CATEGORY, DOCS, EMPTY_FORM,
} from "@/lib/changeForm";

const Section = ({ title, children, hint }) => (
  <Card className="p-5 space-y-4">
    <div><h2 className="font-display font-semibold text-base md:text-lg">{title}</h2>{hint && <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>}</div>
    {children}
  </Card>
);

const Field = ({ label, k, f, set, type = "text", testid }) => (
  <div className="space-y-1">
    <Label className="text-xs">{label}</Label>
    <Input data-testid={testid || `cf-${k}`} type={type} value={f[k] ?? ""} onChange={(e) => set(k, e.target.value)} />
  </div>
);

const fmtUSD = (v) => {
  const n = parseFloat(String(v ?? "").replace(/[^0-9.-]/g, ""));
  return isNaN(n) ? "" : n.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const MoneyField = ({ label, k, f, set }) => (
  <div className="space-y-1">
    <Label className="text-xs">{label}</Label>
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">$</span>
      <Input data-testid={`cf-${k}`} inputMode="decimal" className="pl-7 font-mono-num" value={String(f[k] ?? "").replace(/^\$/, "")}
        onChange={(e) => set(k, e.target.value)} onBlur={(e) => set(k, fmtUSD(e.target.value))} placeholder="0.00" />
    </div>
  </div>
);

const DateField = ({ label, k, f, set }) => (
  <div className="space-y-1">
    <Label className="text-xs">{label}</Label>
    <DatePicker testid={`cf-${k}`} value={f[k] || ""} onChange={(v) => set(k, v)} />
  </div>
);

const Radios = ({ options, k, f, set }) => (
  <div className="flex flex-wrap gap-2">
    {options.map((o) => (
      <button type="button" key={o} data-testid={`cf-${k}-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} onClick={() => set(k, f[k] === o ? "" : o)}
        className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${f[k] === o ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-accent"}`}>{o}</button>
    ))}
  </div>
);

const Checks = ({ options, k, f, set, cols = "sm:grid-cols-3" }) => (
  <div className={`grid grid-cols-2 ${cols} gap-2`}>
    {options.map((o) => {
      const id = `cf-${k}-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
      const on = (f[k] || []).includes(o);
      return (
        <label key={o} htmlFor={id} className="flex items-center gap-2 text-sm cursor-pointer">
          <Checkbox id={id} data-testid={id} checked={on} onCheckedChange={(v) => set(k, v ? [...(f[k] || []), o] : (f[k] || []).filter((x) => x !== o))} />
          {o}
        </label>
      );
    })}
  </div>
);

export default function ChangeRequestForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeCompanyId, activeCompany } = useCompany();
  const [f, setF] = useState({ ...EMPTY_FORM, requested_by: user.name });
  const [employees, setEmployees] = useState([]);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setJob = (row, side, v) => setF((p) => ({ ...p, job_changes: { ...p.job_changes, [row]: { ...(p.job_changes[row] || {}), [side]: v } } }));
  const companyId = user.role === "bureau_admin" ? activeCompanyId : user.company_id;

  useEffect(() => {
    if (!companyId) return;
    api.get("/employees", { params: user.role === "bureau_admin" ? { company_id: companyId } : {} }).then((r) => setEmployees(r.data)).catch(() => {});
  }, [companyId, user.role]);

  useEffect(() => {
    if (id) api.get(`/change-requests/${id}`).then((r) => setF({ ...EMPTY_FORM, ...r.data.form })).catch(() => toast.error("Could not load request"));
  }, [id]);

  const pickEmployee = (eid) => {
    const e = employees.find((x) => x.id === eid);
    if (!e) return;
    setF((p) => ({ ...p, employee_name: e.name, employee_email: e.email || "", employee_id: e.id.slice(-6).toUpperCase(), department: e.department || "", job_title: e.job_title || "",
      employment_status: p.employment_status || (e.employment_type === "Full-time" ? "Full-Time" : e.employment_type === "Part-time" ? "Part-Time" : ""),
      current_rate: fmtUSD(e.pay_type === "salary" ? e.annual_salary : e.hourly_rate) }));
  };

  const submit = async () => {
    if (!f.employee_name) return toast.error("Employee legal name is required");
    if (!f.change_types.length) return toast.error("Select at least one type of change");
    setSaving(true);
    try {
      const body = { company_id: companyId, form: f };
      const r = id ? await api.put(`/change-requests/${id}`, body) : await api.post("/change-requests", body);
      toast.success(id ? "Change request updated" : "Change request submitted to your payroll manager");
      navigate(`/change-requests/${r.data.id}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-5 animate-fade-up max-w-4xl" data-testid="change-request-form">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate("/change-requests")} data-testid="cf-back"><ArrowLeft className="w-5 h-5" /></Button>
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">{id ? "Edit" : "Confidential"} Payroll Change Form</h1>
          <p className="text-sm text-muted-foreground mt-1">{activeCompany?.name} · Complete the fields, check all applicable boxes, and submit before the payroll cutoff.</p>
        </div>
      </div>

      <Section title="Employee Information">
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1 sm:col-span-2">
            <Label className="text-xs">Select employee (prefill)</Label>
            <Select onValueChange={pickEmployee}>
              <SelectTrigger data-testid="cf-employee-select"><SelectValue placeholder="Choose an employee…" /></SelectTrigger>
              <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} — {e.job_title}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Field label="Employee Legal Name *" k="employee_name" f={f} set={set} />
          <Field label="Employee ID" k="employee_id" f={f} set={set} />
          <Field label="Department" k="department" f={f} set={set} />
          <Field label="Job Title" k="job_title" f={f} set={set} />
          <Field label="Work Location" k="work_location" f={f} set={set} />
          <Field label="Supervisor / Manager" k="supervisor" f={f} set={set} />
          <DateField label="Requested Effective Date" k="effective_date" f={f} set={set} />
          <Field label="Pay Period Effective" k="pay_period_effective" f={f} set={set} />
          <DateField label="Date Submitted" k="date_submitted" f={f} set={set} />
          <Field label="Requested By" k="requested_by" f={f} set={set} />
        </div>
        <div className="space-y-1.5"><Label className="text-xs">Current Employment Status</Label><Radios options={EMPLOYMENT_STATUS} k="employment_status" f={f} set={set} />
          {f.employment_status === "Other" && <Input data-testid="cf-employment_status_other" placeholder="Other status" value={f.employment_status_other} onChange={(e) => set("employment_status_other", e.target.value)} />}</div>
        <div className="space-y-1.5"><Label className="text-xs">Pay Frequency</Label><Radios options={PAY_FREQ} k="pay_frequency" f={f} set={set} />
          {f.pay_frequency === "Other" && <Input data-testid="cf-pay_frequency_other" placeholder="Other frequency" value={f.pay_frequency_other} onChange={(e) => set("pay_frequency_other", e.target.value)} />}</div>
      </Section>

      <Section title="Type of Change Requested *">
        <Checks options={CHANGE_TYPES} k="change_types" f={f} set={set} />
        {f.change_types.includes("Other") && <Input data-testid="cf-change_type_other" placeholder="Describe other change" value={f.change_type_other} onChange={(e) => set("change_type_other", e.target.value)} />}
      </Section>

      <Section title="Job, Status, and Schedule Changes">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground text-left"><tr><th className="py-1 pr-3 w-1/3">Field</th><th className="pr-3">Current Information</th><th>New Information</th></tr></thead>
            <tbody>
              {JOB_ROWS.map(([key, label]) => (
                <tr key={key} className="border-t border-border">
                  <td className="py-2 pr-3 font-medium">{label}</td>
                  {["current", "new"].map((side) => (
                    <td key={side} className="py-2 pr-3">
                      {key === "flsa" ? (
                        <Select value={f.job_changes.flsa?.[side] || ""} onValueChange={(v) => setJob("flsa", side, v)}>
                          <SelectTrigger data-testid={`cf-job-flsa-${side}`} className="h-9"><SelectValue placeholder="—" /></SelectTrigger>
                          <SelectContent><SelectItem value="Exempt">Exempt</SelectItem><SelectItem value="Nonexempt">Nonexempt</SelectItem></SelectContent>
                        </Select>
                      ) : (
                        <Input data-testid={`cf-job-${key}-${side}`} className="h-9" value={f.job_changes[key]?.[side] || ""} onChange={(e) => setJob(key, side, e.target.value)} />
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section title="Compensation Change">
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5"><Label className="text-xs">New Pay Basis</Label><Radios options={PAY_BASIS} k="new_pay_basis" f={f} set={set} /></div>
          <div className="space-y-1.5"><Label className="text-xs">Overtime Eligible</Label><Radios options={["Yes", "No"]} k="overtime_eligible" f={f} set={set} /></div>
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          <MoneyField label="Current Rate / Salary" k="current_rate" f={f} set={set} />
          <MoneyField label="New Rate / Salary" k="new_rate" f={f} set={set} />
          <MoneyField label="Increase / Decrease Amount" k="change_amount" f={f} set={set} />
          <Field label="Current Pay Frequency" k="current_pay_frequency" f={f} set={set} />
          <Field label="New Pay Frequency" k="new_pay_frequency" f={f} set={set} />
          <DateField label="Retroactive Effective Date" k="retro_date" f={f} set={set} />
        </div>
      </Section>

      <Section title="One-Time or Recurring Payroll Adjustment">
        <Checks options={ADJ_TYPES} k="adjustment_types" f={f} set={set} />
        {f.adjustment_types.includes("Other") && <Input data-testid="cf-adjustment_other" placeholder="Other adjustment" value={f.adjustment_other} onChange={(e) => set("adjustment_other", e.target.value)} />}
        <div className="grid sm:grid-cols-3 gap-3">
          <MoneyField label="Amount / Rate" k="adj_amount" f={f} set={set} />
          <div className="space-y-1.5"><Label className="text-xs">Frequency</Label><Radios options={ADJ_FREQ} k="adj_frequency" f={f} set={set} /></div>
          <Field label="Start / End Date" k="adj_dates" f={f} set={set} />
          <Field label="Hours / Units" k="adj_hours" f={f} set={set} />
          <Field label="Earning / Deduction Code" k="adj_code" f={f} set={set} />
          <Field label="Cost Center / GL Code" k="adj_gl" f={f} set={set} />
        </div>
      </Section>

      <Section title="Leave, Return, or Termination Details">
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="space-y-1.5"><Label className="text-xs">Action</Label><Radios options={LEAVE_ACTIONS} k="leave_action" f={f} set={set} /></div>
          <div className="space-y-1.5"><Label className="text-xs">Leave Pay Status</Label><Radios options={LEAVE_PAY} k="leave_pay_status" f={f} set={set} /></div>
          <div className="space-y-1.5"><Label className="text-xs">Termination Category</Label><Radios options={TERM_CATEGORY} k="termination_category" f={f} set={set} /></div>
        </div>
        <div className="grid sm:grid-cols-4 gap-3">
          <DateField label="Last Day Worked" k="last_day_worked" f={f} set={set} />
          <DateField label="Effective Date" k="leave_effective_date" f={f} set={set} />
          <DateField label="Return Date" k="return_date" f={f} set={set} />
          <DateField label="Final Pay Date" k="final_pay_date" f={f} set={set} />
        </div>
        <Field label="PTO Payout / Final Instructions" k="pto_payout" f={f} set={set} />
      </Section>

      <Section title="Supporting Documentation">
        <Checks options={DOCS} k="documents" f={f} set={set} cols="sm:grid-cols-4" />
        {f.documents.includes("Other") && <Input data-testid="cf-documents_other" placeholder="Other document" value={f.documents_other} onChange={(e) => set("documents_other", e.target.value)} />}
        <div className="flex gap-2 text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 rounded-lg p-3">
          <ShieldAlert className="w-4 h-4 shrink-0" />
          <span><b>Privacy note:</b> Do not enter full bank account numbers, Social Security numbers, or tax identification numbers on this form. Use the company's approved secure authorization documents.</span>
        </div>
      </Section>

      <Section title="Reason for Change / Business Justification / Additional Instructions" hint="Enter a clear explanation, including any special payroll timing or retroactive calculation instructions.">
        <Textarea data-testid="cf-justification" rows={4} value={f.justification} onChange={(e) => set("justification", e.target.value)} />
      </Section>

      <div className="flex justify-end gap-2 pb-6">
        <Button variant="outline" onClick={() => navigate("/change-requests")}>Cancel</Button>
        <Button data-testid="cf-submit" onClick={submit} disabled={saving}><Send className="w-4 h-4 mr-1.5" /> {saving ? "Submitting…" : id ? "Save & Resubmit" : "Submit to Payroll Manager"}</Button>
      </div>
    </div>
  );
}
